import * as React from 'react';

import {Button} from 'shells/button/button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {FooterBar} from 'shells/modal/footer-bar';

import styles from './bulk-email-send-buttons.module.css';

export type EmailStats = {
    cannotSendCount: number;
    totalCount: number;
    missingFieldsCount: number;
    hasReceivedTemplateRecentlyCount: number;
    missingFieldsAndReceivedRecentlyCount: number;
};

type Props = {
    send: (arg1: {includeIncomplete: boolean; includeRecentlyReceivedTemplate: boolean}) => void;
    hasError: boolean;
    emailStats: EmailStats | null | undefined;
};

export function BulkEmailSendButtons(props: Props) {
    const [includeIncomplete, setIncludeIncomplete] = React.useState<boolean>(false);
    const [includeRecentlyReceivedTemplate, setIncludeRecentlyReceivedTemplate] =
        React.useState<boolean>(false);
    const {emailStats, send} = props;

    if (!emailStats) return null;

    if (hasNoWarnings(emailStats)) {
        return (
            <FooterBar
                right={
                    <Button
                        size='big'
                        variant='primary'
                        onClick={() => {
                            send({
                                includeIncomplete: false,
                                includeRecentlyReceivedTemplate: false,
                            });
                        }}
                        disabled={props.hasError}
                    >
                        Send email to all {emailStats.totalCount} recipients
                    </Button>
                }
            />
        );
    }

    if (canSendToAny(emailStats)) {
        const sendToCount = getSendToCount(emailStats, {
            includeIncomplete,
            includeRecentlyReceivedTemplate,
        });

        return (
            <FooterBar
                left={
                    <div className={styles.checkboxes}>
                        {emailStats.missingFieldsCount > 0 ? (
                            <Checkbox
                                isLarge={true}
                                checked={includeIncomplete}
                                onChange={(isChecked) => {
                                    setIncludeIncomplete(isChecked);
                                }}
                                disabled={props.hasError}
                                label={getIncompleteCheckboxLabel(
                                    emailStats.missingFieldsCount,
                                    emailStats.missingFieldsAndReceivedRecentlyCount
                                )}
                            />
                        ) : null}
                        {emailStats.hasReceivedTemplateRecentlyCount > 0 ? (
                            <Checkbox
                                isLarge={true}
                                checked={includeRecentlyReceivedTemplate}
                                onChange={(isChecked) => {
                                    setIncludeRecentlyReceivedTemplate(isChecked);
                                }}
                                disabled={props.hasError}
                                label={getRecentCheckboxLabel(
                                    emailStats.hasReceivedTemplateRecentlyCount,
                                    emailStats.missingFieldsAndReceivedRecentlyCount
                                )}
                            />
                        ) : null}
                    </div>
                }
                right={
                    <Button
                        size='big'
                        variant='primary'
                        onClick={() => {
                            send({includeIncomplete, includeRecentlyReceivedTemplate});
                        }}
                        disabled={sendToCount <= 0 || props.hasError}
                    >
                        Send to {sendToCount} {getRecipientLabel(sendToCount)}
                    </Button>
                }
            />
        );
    }

    // No valid recipients
    return (
        <FooterBar
            right={
                <Button size='big' variant='primary' onClick={() => {}} disabled={true}>
                    No valid recipients
                </Button>
            }
        />
    );
}

export function hasNoWarnings(emailStats: EmailStats) {
    return (
        emailStats.cannotSendCount === 0 &&
        emailStats.missingFieldsCount === 0 &&
        emailStats.hasReceivedTemplateRecentlyCount === 0
    );
}

export function canSendToAny(emailStats: {totalCount: number; cannotSendCount: number}) {
    return getSendableCount(emailStats) > 0 && emailStats.totalCount > 0;
}

// @ts-expect-error
function getRecipientLabel(qty): string {
    return qty === 0 || qty > 1 ? 'recipients' : 'recipient';
}

function getSendableCount(emailStats: {totalCount: number; cannotSendCount: number}) {
    return emailStats.totalCount - emailStats.cannotSendCount;
}

export function getSendToCount(
    emailStats: EmailStats,
    options: {
        includeIncomplete: boolean;
        includeRecentlyReceivedTemplate: boolean;
    }
): number {
    const {includeIncomplete, includeRecentlyReceivedTemplate} = options;
    const sendableCount = getSendableCount(emailStats);
    let count = sendableCount;
    if (!includeIncomplete) {
        count = count - emailStats.missingFieldsCount;
    }
    if (!includeRecentlyReceivedTemplate) {
        count = count - emailStats.hasReceivedTemplateRecentlyCount;
    }
    if (!includeIncomplete && !includeRecentlyReceivedTemplate) {
        count = count + emailStats.missingFieldsAndReceivedRecentlyCount;
    }

    return Math.max(count, 0);
}

export function getIncompleteCheckboxLabel(incompleteCount: number, bothCount: number) {
    const recipientLabel = bothCount ? 'recipients' : getRecipientLabel(incompleteCount);
    // We don't want to show a count if there is overlap between sent recently and incomplete.  It makes things confusing.
    const countLabel = bothCount ? 'Include' : `Include ${incompleteCount}`;

    return `${countLabel} ${recipientLabel} missing template requirements`;
}

export function getRecentCheckboxLabel(recentCount: number, bothCount: number) {
    const recipientLabel = bothCount ? 'recipients' : getRecipientLabel(recentCount);

    // We don't want to show a count if there is overlap between sent recently and incomplete.  It makes things confusing.
    const countLabel = bothCount ? 'Include' : `Include ${recentCount}`;

    return `${countLabel} ${recipientLabel} who recently received this template`;
}
